import type { NotificationLinkType } from '@palaccount/contracts';

/**
 * BR-ADM-04-06's second half — "…and OPEN THE RECORD IT NAMES."
 *
 * The server answers `open` with a link TYPE and an id and deliberately not
 * with a URL: routes are a client concern (§3.1.4 says the URLs are a build
 * choice), and an API that returned paths would make every route rename a
 * breaking API change and would let a stored row outlive the screen it names.
 *
 * A link type with no entry here is a row that cannot be opened — the exact
 * defect BR-ADM-04-06 was written against — so the map is TOTAL over
 * `NotificationLinkType` and the compiler says so.
 */
export function pathForNotification(
  linkType: NotificationLinkType,
  linkId: string | null,
): string {
  switch (linkType) {
    // SAL-04's editor doubles as the read view (`/sales/:invoiceId`).
    case 'invoice':
      return linkId ? `/sales/${linkId}` : '/sales';
    // MD-01's partner detail, which carries the CRM-04 statement section.
    case 'partner':
      return linkId ? `/partners/${linkId}` : '/partners';
    case 'item':
      return linkId ? `/inventory/items/${linkId}` : '/inventory';
    // FIN-10 / FIN-11 are two registers, not one screen with a filter, so the
    // link type carries the direction rather than the params. `?check=` is
    // read by ChecksPage, which opens that row's panel.
    case 'incoming_check':
      return linkId ? `/banks/checks/incoming?check=${linkId}` : '/banks/checks/incoming';
    case 'outgoing_check':
      return linkId ? `/banks/checks/outgoing?check=${linkId}` : '/banks/checks/outgoing';
    // EC-15-12's aggregates point at a LIST. INV-05's stock balances screen
    // carries the low-stock filter, which is what the sentence is about.
    case 'stock_low':
      return '/inventory/stock?filter=low';
    case 'stock_expiring':
      return linkId ? `/inventory/items/${linkId}` : '/inventory/stock';
    case 'leave_request':
      return '/employees/leave';
    /**
     * CRM-03 lives at `/partners/followups` (router.tsx) and opens on **My
     * Tasks**, which is scoped to the signed-in user — and a `task_reminder`
     * only ever reaches its own ASSIGNEE, so the reminded task is on the
     * landing view without a filter, a tab or a query.
     *
     * NO ID IN THE PATH, and that is the same choice `stock_low` makes rather
     * than a shortcut. CRM-03 has no per-task route and no panel a query
     * string opens; inventing `?task=<id>` that nothing reads would be a link
     * CLAIMING to open a record and not opening it, which is the exact defect
     * BR-ADM-04-06 was written against. The id is still on the row, so the
     * open path's existence check and the audit target both have it.
     */
    case 'task':
      return '/partners/followups';
  }
}
